/**
 * ArtworkSkeleton - Placeholder skeleton for loading states
 * 
 * Displays an animated shimmer placeholder while artwork cards
 * are loading. Matches the visual structure of the actual card.
 */
"use client";

import clsx from "clsx";

interface ArtworkSkeletonProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * Single artwork card skeleton
 */
export function ArtworkSkeleton({ className }: ArtworkSkeletonProps) {
  return (
    <article
      className={clsx(
        "relative mb-3 overflow-hidden rounded-lg border border-white/10 bg-brand-surface",
        "animate-pulse",
        className
      )}
    >
      {/* Image placeholder - random aspect ratio for visual variety */}
      <div 
        className="w-full bg-white/5"
        style={{ 
          paddingBottom: `${Math.floor(Math.random() * 40) + 80}%` // 80-120% aspect ratio
        }}
      />
      
      {/* Shimmer overlay */}
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/5 to-transparent" />
    </article>
  );
}

/**
 * Grid of artwork skeletons
 */
export function ArtworkSkeletonGrid({ count = 12 }: { count?: number }) {
  // Generate consistent heights for skeleton items
  const items = Array.from({ length: count }, (_, i) => ({
    id: `skeleton-${i}`,
    height: 80 + (i % 4) * 20 // 80%, 100%, 120%, 140%
  }));

  return (
    <>
      {items.map((item) => (
        <article
          key={item.id}
          className="relative mb-3 overflow-hidden rounded-lg border border-white/10 bg-brand-surface animate-pulse"
        >
          <div 
            className="w-full bg-white/5"
            style={{ paddingBottom: `${item.height}%` }}
          />
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/5 to-transparent" />
        </article>
      ))}
    </>
  );
}

export default ArtworkSkeleton;
